import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Agenda, AgendaRequest } from '../../../../../models/agenda.interface';
import { Post } from '../../../../../models/rest.interface';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import {
  alunoResponse,
  ListarAlunosParams,
} from '../../../../../models/alunos.interface';
import { PaginacaoResponse } from '../../../../../models/paginacaoResponse.interface';
import { UserService } from '../../../../../shared/services/user/user.service';

const URL_BASE = `${environment.BACKEND_API}/agendas`;
const URL = `${environment.BACKEND_API}/upload`;

@Injectable({
  providedIn: 'root',
})
export class AgendaService {
  public cadastroCompleto: EventEmitter<void> = new EventEmitter();
  escolaId!: number;

  constructor(
    private readonly http: HttpClient,
    private readonly userService: UserService,
  ) {

    this.userService.escola.subscribe((value) => {
      if (value !== undefined) {
        this.escolaId = value.id;
      }
    });
  }

  cadastrar(agenda: Post<AgendaRequest>): Observable<Post<Agenda>> {
    return this.http.post<Post<Agenda>>(`${URL_BASE}`, agenda)
      .pipe(
        catchError(this.handleError)
      );
  }

  salvarArquivoAgenda(formData: FormData): Observable<any> {
    return this.http.post<any>(`${URL}`, formData)
      .pipe(
        catchError(this.handleError)
      );
  }

  listarPorEscolas(
    params: ListarAlunosParams = {},
  ): Observable<PaginacaoResponse<Agenda>> {
    let httpParams = new HttpParams()
      .set('pagination[withCount]', 'true')
      .set('pagination[page]', (params.page ?? 1).toString())
      .set('pagination[pageSize]', (params.pageSize ?? 99999999).toString())
      .set('populate[0]', 'arquivo')
      .set('populate[1]', 'escola')
      .set('sort[0]', 'data:desc');

    return this.http.get<PaginacaoResponse<Agenda>>(
      `${URL_BASE}/?filters[escola][$eq]=${this.escolaId}`,
      { params: httpParams },
    ).pipe(
      catchError(this.handleError)
    );
  }

  buscar(documentId: string): Observable<Post<Agenda>> {
    return this.http.get<Post<Agenda>>(`${URL_BASE}/${documentId}?populate[0]=arquivo&populate[1]=escola`)
      .pipe(
        catchError(this.handleError)
      );
  }

  atualizar(documentId: string, agenda: Partial<AgendaRequest>): Observable<Post<Agenda>> {
    return this.http.put<Post<Agenda>>(`${URL_BASE}/${documentId}`, agenda)
      .pipe(
        catchError(this.handleError)
      );
  }

  excluir(documentId: string): Observable<void> {
    return this.http.delete<void>(`${URL_BASE}/${documentId}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ocorreu um erro inesperado.';

    if (error.error instanceof ErrorEvent) {
      // Erro do cliente
      errorMessage = `Erro: ${error.error.message}`;
    } else {
      // Erro do servidor
      switch (error.status) {
        case 400:
          errorMessage = 'Dados inválidos. Verifique as informações enviadas.';
          break;
        case 401:
          errorMessage = 'Não autorizado. Faça login novamente.';
          break;
        case 403:
          errorMessage = 'Acesso negado. Você não tem permissão para esta ação.';
          break;
        case 404:
          errorMessage = 'Recurso não encontrado.';
          break;
        case 409:
          errorMessage = 'Conflito de dados. Verifique as informações.';
          break;
        case 422:
          errorMessage = 'Dados inválidos. Verifique o formulário.';
          break;
        case 500:
          errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
          break;
        default:
          errorMessage = `Erro ${error.status}: ${error.message}`;
      }
    }

    console.error('Erro na requisição:', error);
    return throwError(() => new Error(errorMessage));
  }

  // Método para validar dados antes do envio
  validarDadosAgenda(agenda: AgendaRequest): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!agenda.assunto || agenda.assunto.trim().length === 0) {
      errors.push('Assunto é obrigatório');
    }

    if (!agenda.descricao || agenda.descricao.trim().length === 0) {
      errors.push('Descrição é obrigatória');
    }

    if (!agenda.data) {
      errors.push('Data do evento é obrigatória');
    }

    if (!agenda.dataLembrete) {
      errors.push('Data de notificação é obrigatória');
    }

    if (!agenda.categoria) {
      errors.push('Categoria é obrigatória');
    }

    if (!agenda.enviarPara) {
      errors.push('Destinatários são obrigatórios');
    }

    // Validar horários se não for dia inteiro
    if (!agenda.diaInteiro) {
      if (!agenda.horaInicio || !agenda.horaFim) {
        errors.push('Horário de início e fim são obrigatórios para eventos com horário específico');
      } else if (agenda.horaInicio >= agenda.horaFim) {
        errors.push('Hora de fim deve ser posterior à hora de início');
      }
    }

    // Validar turmas se for enviar para pais
    if (agenda.enviarPara === 'Pais' && !agenda.enviarTodasTurmas) {
      if (!agenda.idsTurmas || agenda.idsTurmas.length === 0) {
        errors.push('Selecione pelo menos uma turma ou marque "Todas"');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}
